"use client";

import { useEffect, useRef, useState } from "react";
import DatePicker, { DateObject, type DatePickerRef } from "react-multi-date-picker";
import gregorian from "react-date-object/calendars/gregorian";
import persian from "react-date-object/calendars/persian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import persian_fa from "react-date-object/locales/persian_fa";
import { CalendarDays } from "lucide-react";
import "./date-field.css";

type Props = {
  label: string;
  /** Gregorian "YYYY-MM-DD", or "" for none — the API's own format. */
  value: string;
  onChange: (value: string) => void;
  lang: "fa" | "en";
  /** Which of the field's edges the calendar lines up with — the outer one,
   *  so on a phone neither calendar is pushed against the screen edge. */
  edge?: "start" | "end";
};

/**
 * A v2 date field over the same picker the legacy page used
 * (`react-multi-date-picker`): Jalali in Persian, Gregorian in English, and
 * always a Gregorian "YYYY-MM-DD" out, exactly what `LocalizedDatePicker`
 * sent. The trigger is the SortMenu trigger's face; the calendar is re-set in
 * v2 tokens by `.nt-cal` (date-field.css).
 */
export default function DateField({ label, value, onChange, lang, edge = "start" }: Props) {
  const isFa = lang === "fa";
  const picker = useRef<DatePickerRef>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  // The picker closes on an outside click but not on Escape; a v2 panel must do both.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      picker.current?.closeCalendar();
      trigger.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const date = value
    ? new DateObject({ date: value, format: "YYYY-MM-DD", calendar: gregorian, locale: gregorian_en })
    : null;

  return (
    <DatePicker
      ref={picker}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={date && isFa ? date.convert(persian, persian_fa) : date}
      onChange={(d: DateObject | null) => onChange(d ? new DateObject(d).convert(gregorian, gregorian_en).format("YYYY-MM-DD") : "")}
      calendar={isFa ? persian : gregorian}
      locale={isFa ? persian_fa : gregorian_en}
      format={isFa ? "YYYY/MM/DD" : "YYYY-MM-DD"}
      calendarPosition={(edge === "start") === isFa ? "bottom-right" : "bottom-left"}
      className="nt-cal"
      containerClassName="nt-date-wrap"
      arrow={false}
      editable={false}
      // The picker's default picks TODAY the moment it opens — which filtered
      // the list before anything was chosen (the legacy page did that too).
      onOpenPickNewDate={false}
      render={(shown: string, openCalendar: () => void) => (
        <button ref={trigger} type="button" className="nt-date v2-focus" onClick={openCalendar} aria-expanded={open} data-set={shown ? true : undefined}>
          <CalendarDays aria-hidden />
          <span className="nt-date-k">{shown ? label : `${label} ${isFa ? "تاریخ" : "date"}`}</span>
          {shown ? <span className="nt-date-v">{shown}</span> : null}
        </button>
      )}
    />
  );
}
